/* global ACCEPTED_ATTACHMENT_FILE_TYPES */

import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

import { Formik, Field } from 'formik';
import moment from 'moment';
import * as Yup from 'yup';

import MilestonesEdit from '../milestones/procurement_milestones/MilestonesEdit';
import DangerAlert from '../partial/alerts/DangerAlert';

import TicketingCommitmentLinesTable from './TicketingCommitmentLinesTable';
import TicketingImpactHeader from './TicketingImpactHeader';
import TicketingsCommitmentContract from './TicketingsCommitmentContract';

import { formatMoneyWithLocale } from 'Helpers/formatService';

import Button from 'CommonComponents/Button';
import CheckboxFormField from 'CommonComponents/CheckboxFormField';
import Tooltip from 'CommonComponents/Tooltip';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

// owner_id defaults to the current user and is never empty, so it is NOT required.
// description is optional. The amount / milestone gates live on the submit button (and are
// re-checked server-side), not in this schema.
const TicketingCommitSchema = Yup.object().shape({
    owner_id: Yup.string(),
    description: Yup.string(),
    supplier_id: Yup.string().required($t('This value is required')),
    contract_id: Yup.string(),
    contract_started_at: Yup.string(),
    contract_ended_at: Yup.string(),
    contract_signed: Yup.boolean(),
    contract_closed: Yup.boolean(),
});

export default function TicketingCommitForm(props) {
    const [user, setUser] = useState({});
    const [submissionDate, setSubmissionDate] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [selectedFiles, setSelectedFiles] = useState([]);

    // The single commitment line is one total amount. Held as a raw string so a half-typed
    // value ("", "1.") survives editing instead of snapping to 0.
    const [amount, setAmount] = useState('');

    // Milestone section validity / autosave state lifted from MilestoneSectionForm, so the
    // single commit action can be gated on the drafted milestones totalling 100%.
    const [sectionValidity, setSectionValidity] = useState({});
    const [presentSections, setPresentSections] = useState([]);
    const [milestoneSaving, setMilestoneSaving] = useState({});

    const [error, setError] = useState(null);

    const toast = useToast();
    const { users } = useUsers();

    const budgetedValue = props.valueTotals?.budgeted ?? 0;
    const uncommittedValue = props.valueTotals?.uncommitted ?? 0;

    // Default the line to the whole remaining balance, so the screen opens pre-loaded (no
    // "select then Next" dance) and a plain commit takes the full balance.
    useEffect(() => {
        fetchSuppliers();
        setSubmissionDate(moment().format('DD/MM/YYYY'));
        setAmount(String(uncommittedValue));
    }, []);

    // The Requestor defaults to the logged-in user, but the event user list (useUsers) does not
    // always contain them (e.g. a super-admin who is not an event member). Merge the current user
    // in so they are always selectable and the pre-selected owner_id resolves to a visible label.
    const requestorUsers = useMemo(() => {
        if (user && user.id && !users.some((u) => u.id === user.id)) {
            return [...users, user];
        }
        return users;
    }, [users, user]);

    const fetchSuppliers = () => {
        axios
            .get(props.refreshCommitmentDataUrl, { params: { fields: 'slim' } })
            .then(({ data }) => {
                setUser({ ...data.user });
                setSuppliers([...data.suppliers]);
            })
            .catch((e) => console.log(e));
    };

    const committingValue = Number(amount) || 0;

    // Over-commitment is HARD-BLOCKED here, a deliberate divergence from Direct Commitment's
    // warn-and-permit: the balance may reach exactly zero but never go negative. The server
    // re-checks this inside the commit transaction, so this is UX, not the enforcement point.
    const overCommitted = committingValue > uncommittedValue;
    const amountInvalid = committingValue <= 0 || overCommitted;

    // This screen always renders exactly one (cash) milestone section, so "no section has
    // reported in yet" means the draft is still loading rather than "there is nothing to gate
    // on". Treating an empty set as valid — as the Direct Commitment composition does, where it
    // is a genuine possibility — would leave Commit briefly enabled while the section still
    // shows "Loading...", letting a click through before the milestones are known.
    const milestonesLoaded = presentSections.length > 0;

    // All present milestone sections must total 100%. Scope every read to presentSections so a
    // stale validity / saving key left by a dropped section can never gate the button.
    const milestonesValid = milestonesLoaded && presentSections.every((section) => sectionValidity[section] === true);
    const milestoneSaveInFlight = presentSections.some((section) => milestoneSaving[section] === true);

    const submitDisabled = amountInvalid || !milestonesLoaded || milestonesValid !== true || milestoneSaveInFlight;

    const blockedMessage = useMemo(() => {
        if (overCommitted) {
            return $t('You cannot commit more than the {{amount}} uncommitted amount remaining.', { amount: formatMoneyWithLocale(uncommittedValue) });
        }
        if (committingValue <= 0) {
            return $t('Enter a committing amount greater than zero.');
        }
        if (!milestonesLoaded) {
            return $t('Loading cash receipt milestones…');
        }
        if (!milestonesValid) {
            return $t('Total percentage must equal 100');
        }
        return '';
    }, [overCommitted, committingValue, milestonesLoaded, milestonesValid, uncommittedValue]);

    const handleMilestoneSavingChange = (section, isSaving) => {
        setMilestoneSaving((prev) => (prev[section] === isSaving ? prev : { ...prev, [section]: isSaving }));
    };

    const handleValidityChange = (section, isValid) => {
        setSectionValidity((prev) => (prev[section] === isValid ? prev : { ...prev, [section]: isValid }));
    };

    const handleSectionPresent = (section) => {
        setPresentSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    };

    const changeFilesHandler = (event) => {
        setSelectedFiles(event.target.files);
    };

    // Cancel must persist nothing — including the drafted milestones this screen seeded.
    const handleCancel = () => {
        const redirect = () => (window.location.href = props.backUrl);

        if (!props.cancelUrl) {
            redirect();
            return;
        }

        axios
            .put(props.cancelUrl, {}, { headers: { ...props.mutationHeader } })
            .catch((e) => console.log(e))
            .finally(redirect);
    };

    // Requestor / submission date are portaled into the page header (right of the page title) so
    // they sit alongside the "Commit Ticketing" title while staying inside the Formik tree.
    const renderHeaderMeta = (content) => {
        const target = typeof document !== 'undefined' ? document.getElementById('ticketing-commit-header-meta') : null;
        if (target) {
            return ReactDOM.createPortal(content, target);
        }
        return <div className="tw-flex tw-justify-end tw-p-6 tw-border-b tw-border-charcoal-15">{content}</div>;
    };

    return (
        <div className="tw-pb-8">
            <Formik
                initialValues={{
                    owner_id: props.currentUserId ?? '',
                    description: '',
                    supplier_id: '',
                    contract_id: '',
                    contract_started_at: '',
                    contract_ended_at: '',
                    contract_signed: false,
                    contract_closed: false,
                }}
                validationSchema={TicketingCommitSchema}
                onSubmit={(values, actions) => {
                    setError(null);

                    const formData = new FormData();
                    formData.append('owner_id', values.owner_id ?? '');
                    formData.append('description', values.description ?? '');
                    formData.append('amount', '' + committingValue);
                    formData.append('supplier_id', values.supplier_id);
                    if (values.contract_id) {
                        formData.append('contract_id', values.contract_id);
                    }
                    formData.append('contract_started_at', values.contract_started_at ? moment(values.contract_started_at).format('YYYY-MM-DD') : '');
                    formData.append('contract_ended_at', values.contract_ended_at ? moment(values.contract_ended_at).format('YYYY-MM-DD') : '');
                    formData.append('contract_signed', values.contract_signed ? '1' : '0');
                    formData.append('contract_closed', values.contract_closed ? '1' : '0');

                    for (let i = 0; i < selectedFiles.length; i++) {
                        formData.append(`files[${i}]`, selectedFiles[i]);
                    }

                    axios
                        .post(props.storeUrl, formData, { headers: { ...props.mutationHeader } })
                        .then(() => {
                            toast.success($t('Ticketing committed successfully'));
                            setTimeout(() => (window.location.href = props.backUrl), 2000);
                        })
                        .catch((e) => {
                            console.log(e);
                            setError(e?.response?.data?.errors);
                            e?.response?.data?.errors && actions.setErrors(e.response.data.errors);
                        })
                        .finally(() => actions.setSubmitting(false));
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                    <div className="card front bg-white border-0 rounded-15px shadow tw-overflow-hidden">
                        {renderHeaderMeta(
                            <div className="tw-flex tw-items-start tw-gap-6">
                                <div>
                                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">
                                        {$t('Requestor')}
                                    </div>
                                    <div className="tw-min-w-[12rem]">
                                        <UserSelectorInput
                                            selectedId={values.owner_id}
                                            usersList={requestorUsers}
                                            onChange={(value) => setFieldValue('owner_id', value)}
                                            fetchUrl={props.listUsersUrl}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">
                                        {$t('Submission Date')}
                                    </div>
                                    <div className="tw-text-sm tw-font-semibold tw-text-charcoal tw-pt-2">{submissionDate}</div>
                                </div>
                            </div>,
                        )}

                        {/* Hero: uncommitted value impact */}
                        <div className="tw-p-6 tw-border-b tw-border-charcoal-15">
                            <TicketingImpactHeader
                                context={{ crumbs: $t('Revenue › Ticketing'), line: $t('Ticketing') }}
                                now={uncommittedValue}
                                committing={committingValue}
                                blocked={overCommitted}
                            />
                        </div>

                        {/* Body: two columns. Splits at xl (not lg, which DirectCommitForm uses):
                            the sidebar eats ~300px, so between lg and xl two columns force the
                            milestone table wider than the viewport and the whole card overflows
                            horizontally. min-w-0 lets each column shrink below its content width. */}
                        <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-6 tw-p-6">
                            {/* LEFT: commitment line + cash receipt milestones */}
                            <div className="tw-flex tw-flex-col tw-gap-y-6 tw-min-w-0">
                                <section>
                                    <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                        {$t('Commitment')}{' '}
                                        <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal">
                                            — {$t('edit the committing amount inline')}
                                        </span>
                                    </h3>
                                    <TicketingCommitmentLinesTable
                                        budgeted={budgetedValue}
                                        amount={amount}
                                        amountInvalid={amountInvalid}
                                        onAmountChange={setAmount}
                                    />
                                </section>

                                {/* overflow-x-auto: the shared milestone table has a real minimum width
                                    (drag handle, date, description, %, value, delete) — if a viewport is
                                    ever too narrow for it, it scrolls inside this section rather than
                                    stretching the page. */}
                                <section className="tw-min-w-0 tw-overflow-x-auto">
                                    <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                        {$t('Cash Receipt Milestones')}{' '}
                                        <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal">
                                            — {$t('inherited from the event cashflow, editable')}
                                        </span>
                                    </h3>
                                    {/* Ticketing is cash-only: vikEnabled is hard-false so no VIK section
                                        can ever render, whatever the event-level VIK setting says. */}
                                    <MilestonesEdit
                                        getMilestonesUrl={props.getRevenueMilestonesUrl}
                                        minDate={props.minDate}
                                        maxDate={props.maxDate}
                                        parentSection="ticketing-commit"
                                        hideSectionHeader
                                        totalValue={committingValue}
                                        committingCash={committingValue}
                                        committingVik={0}
                                        vikEnabled={false}
                                        isSliderAllowed={false}
                                        allowConfirm={false}
                                        preserveDraftMilestones={true}
                                        showCancelButton={false}
                                        onValidityChange={handleValidityChange}
                                        onSectionPresent={handleSectionPresent}
                                        onAutosaveStateChange={handleMilestoneSavingChange}
                                    />
                                </section>
                            </div>

                            {/* RIGHT: supplier + description + attachments */}
                            <div className="tw-flex tw-flex-col tw-gap-y-6 tw-min-w-0">
                                <section>
                                    <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">{$t('Supplier')}</h3>
                                    <TicketingsCommitmentContract
                                        suppliers={suppliers}
                                        errors={errors}
                                        touched={touched}
                                        values={values}
                                        onSupplierCreated={fetchSuppliers}
                                        suppliersIndexUrl={props.suppliersIndexUrl}
                                        suppliersStoreUrl={props.suppliersStoreUrl}
                                        suppliersCreateUrl={props.suppliersCreateUrl}
                                        setFieldValue={setFieldValue}
                                    />
                                    <div className="tw-flex tw-gap-x-8 tw-mt-4">
                                        <CheckboxFormField name="contract_signed" id="contract_signed" type="checkbox" label={$t('Contract Signed')} />
                                        <CheckboxFormField name="contract_closed" id="contract_closed" type="checkbox" label={$t('Contract Closed')} />
                                    </div>
                                </section>

                                <section>
                                    <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                        {$t('Commitment description')}{' '}
                                        <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal tw-bg-charcoal-15 tw-rounded tw-px-1.5 tw-py-0.5">
                                            {$t('Optional')}
                                        </span>
                                    </h3>
                                    <Field
                                        as="textarea"
                                        className="form-control !tw-bg-grey/50 tw-border-0 tw-rounded-lg"
                                        name="description"
                                        rows={3}
                                        placeholder={$t('Please provide an overview of the ticketing commitment here')}
                                    />
                                </section>

                                <section>
                                    <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">{$t('Attachments')}</h3>
                                    <input
                                        id="ticketing-commitment-attachments"
                                        type="file"
                                        name="files[]"
                                        multiple
                                        className="file-input"
                                        placeholder={$t('Select Files')}
                                        accept={typeof ACCEPTED_ATTACHMENT_FILE_TYPES !== 'undefined' ? ACCEPTED_ATTACHMENT_FILE_TYPES : undefined}
                                        onChange={changeFilesHandler}
                                    />
                                </section>
                            </div>
                        </div>

                        {/* Over-commit is blocked, not warned about: the commit action is disabled below. */}
                        {overCommitted && (
                            <div className="tw-mx-6 tw-mb-2 tw-rounded-lg tw-border tw-border-red tw-bg-red-10 tw-px-4 tw-py-3 tw-text-sm tw-text-red tw-font-medium">
                                {$t('You cannot commit more than the {{amount}} uncommitted amount remaining.', {
                                    amount: formatMoneyWithLocale(uncommittedValue),
                                })}
                            </div>
                        )}

                        {!!error && _.isObject(error) && (
                            <div className="tw-px-6">
                                {Object.values(error)[0].map((e, i) => (
                                    <DangerAlert key={i} message={e} />
                                ))}
                            </div>
                        )}

                        {/* Action bar: single commit action */}
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-6 tw-border-t tw-border-charcoal-15 tw-bg-charcoal-10">
                            <p className="tw-text-xs tw-text-charcoal-50 tw-max-w-xl tw-mb-0">
                                {$t('The full audit trail — user, timestamp, prior and new uncommitted amounts — is captured on submit.')}
                            </p>
                            <div className="tw-flex tw-gap-x-3">
                                <Button variant="secondary" disabled={isSubmitting} onClick={handleCancel}>
                                    {$t('Cancel')}
                                </Button>
                                <Tooltip placement="top" tooltipType="text" tooltipContent={blockedMessage} allowTooltip={submitDisabled}>
                                    <span className="tw-inline-block">
                                        <Button variant="primary" disabled={isSubmitting || submitDisabled} onClick={handleSubmit}>
                                            {$t('Commit Ticketing')}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </div>
                        </div>
                    </div>
                )}
            </Formik>
        </div>
    );
}
